import { useQuery } from '@apollo/client';
import { Button } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { usePersistentSnackbar } from '~/api/helpers';
import { useCreateShow } from '~/api/shows';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import type { Orientation } from '~/generated/graphql';
import { assert } from '~/lib/assert';
import { CreateShowWizardDocument } from '../queries/queries.generated';
import { NameInput, NameStep, StepText, StepTitle, Stepped } from './wizard';
import { WizardLayoutGrid, WizardOrientationGrid } from './wizard-cards';

export const useCreateShowWizard = () => {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  const openWizard = useCallback(() => setOpen(true), []);

  return {
    open: openWizard,
    props: { close, open },
  };
};

export interface CreateShowWizardProps {
  close: () => void;
  open: boolean;
}

export const CreateShowWizard = ({ close, open }: CreateShowWizardProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [orientation, setOrientation] = useState<Orientation>('LANDSCAPE');
  const [layoutId, setLayoutId] = useState<number | ''>('');
  const { showFolderId } = useParams<{ showFolderId?: string }>();
  const folderId = showFolderId ? Number(showFolderId) : undefined;

  const { data } = useQuery(CreateShowWizardDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const [createShow] = useCreateShow();

  const persistentSnackbar = usePersistentSnackbar();

  const layouts =
    data?.network?.allLayouts.filter((layout) => layout.orientation === orientation) ?? [];

  const firstLayoutId = layouts[0]?.id ?? '';

  useEffect(() => {
    if (typeof firstLayoutId !== 'number') return;
    setLayoutId(firstLayoutId);
  }, [firstLayoutId]);

  const reset = () => {
    setStep(0);
    setName('');
    setOrientation('LANDSCAPE');
    setLayoutId(firstLayoutId);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={close}
      open={open}
      TransitionProps={{ onExited: reset }}
    >
      <DialogTitle onClose={close}>{t('pages.shows.wizards.create_show.title')}</DialogTitle>

      <DialogContent>
        <StepText
          activeStep={step}
          nameText={t('pages.shows.wizards.create_show.descriptions.name')}
          orientationText={t('pages.shows.wizards.create_show.descriptions.orientation')}
          layoutText={t('pages.shows.wizards.create_show.descriptions.layout')}
        />

        <StepTitle activeStep={step} />

        <NameStep activeStep={step} step={0}>
          <NameInput
            autoFocus
            margin="dense"
            onChange={(event) => setName(event.target.value)}
            value={name}
          />
        </NameStep>

        <Stepped activeStep={step} step={1}>
          <WizardOrientationGrid onClick={setOrientation} selected={orientation} />
        </Stepped>

        <Stepped activeStep={step} step={2}>
          <WizardLayoutGrid layouts={layouts} onClick={setLayoutId} selected={layoutId} />
        </Stepped>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={close}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={(step === 0 && name.trim() === '') || step > 2}
          onClick={async () => {
            setStep((prevStep) => prevStep + 1);
            if (step < 2) return;
            // Do the create
            try {
              assert(typeof layoutId === 'number', 'Layout not set');

              const mutation = await createShow({
                variables: {
                  input: { layoutId, name, networkId: currentNetwork.id, showFolderId: folderId },
                },
              });

              navigate(`show/${mutation.data?.createShow?.show.id}/design`);
            } catch (error) {
              const message = error instanceof Error ? error.message : String(error);
              persistentSnackbar(t('pages.shows.wizards.create_show.error_message', { message }), {
                reload: true,
              });
            }
          }}
          variant="contained"
        >
          {step === 2 ? t('common.dialogs.create') : t('common.buttons.next')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
